var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { translate } from '@utils/i18n';
import { Tag } from '@ui/atoms/Tag';
import { Text } from '@ui/atoms/Text';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import { listCheckConfigs, createCheckConfig, deleteCheckConfig } from '../../../TenantKnowledgeBase/services/TkbService';
import useUpdateAIInspectionManager from '../../hooks/useUpdateAIInspectionManager';
import { CHECK_CARDS } from './exclusions/checkExclusionConfig';
import { AddExclusionModal } from './exclusions/AddExclusionModal';
const namespace = 'features.settings.ai_inspection_manager';
const t = (key, params = {}) => translate(key, Object.assign({ namespace }, params));
const CHECK_CONFIGS_KEY = ['aim', 'check_exclusions'];
// Scope prefix for the chip label, e.g. "Account: Walmart". tenant is never
// offered as an exclusion axis but is included for exhaustive typing.
const SCOPE_PREFIX_KEY = {
    account: 'check_exclusions.scope_level_options.account',
    account_tag: 'check_exclusions.scope_level_options.account_tag',
    building: 'check_exclusions.scope_level_options.building',
    building_tag: 'check_exclusions.scope_level_options.building_tag',
    tenant: 'check_exclusions.scope_level_options.account'
};
function IssuesTypeFlags({ settingsData, accounts, accountTags, buildingTags }) {
    var _a;
    const { updateSettings } = useUpdateAIInspectionManager();
    const qc = useQueryClient();
    const [openCardKey, setOpenCardKey] = useState(null);
    const [addError, setAddError] = useState('');
    // Keyed by card key — mirrors addError but scoped per-card since delete can
    // be triggered from any card's chip row while the modal is closed.
    const [deleteErrorByCard, setDeleteErrorByCard] = useState({});
    const { data } = useQuery({
        queryKey: CHECK_CONFIGS_KEY,
        queryFn: listCheckConfigs
    });
    const invalidate = useCallback(() => {
        void qc.invalidateQueries({ queryKey: CHECK_CONFIGS_KEY });
    }, [qc]);
    // Exclusions are ia_check_config rows with enabled=false only.
    const exclusionConfigs = useMemo(() => { var _a; return ((_a = data === null || data === void 0 ? void 0 : data.check_configs) !== null && _a !== void 0 ? _a : []).filter((cc) => cc.enabled === false); }, [data]);
    // Group exclusion rows into per-card, per-scope chips.
    const chipsByCard = useMemo(() => {
        const result = {};
        CHECK_CARDS.forEach((card) => {
            if (card.issueTypes.length === 0)
                return;
            const issueTypeSet = new Set(card.issueTypes);
            const byScope = new Map();
            exclusionConfigs
                .filter((cc) => issueTypeSet.has(cc.issue_type) && cc.scope_key != null)
                .forEach((cc) => {
                var _a;
                const scopeKey = cc.scope_key;
                const mapKey = `${cc.scope_level}:${scopeKey}`;
                const existing = byScope.get(mapKey);
                if (existing) {
                    existing.configIds.push(cc.id);
                    return;
                }
                const prefix = t(SCOPE_PREFIX_KEY[cc.scope_level]);
                byScope.set(mapKey, {
                    scopeLevel: cc.scope_level,
                    scopeKey,
                    label: `${prefix}: ${(_a = cc.scope_label) !== null && _a !== void 0 ? _a : scopeKey}`,
                    configIds: [cc.id]
                });
            });
            result[card.key] = Array.from(byScope.values());
        });
        return result;
    }, [exclusionConfigs]);
    const createMutation = useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ card, scopeLevel, values }) {
            // Fan out: one row per (scope value × issue type). createCheckConfig is
            // single-row (no batch endpoint — see TkbService), so we issue N calls and
            // tolerate partial failure via allSettled.
            const payloads = values.flatMap((scopeKey) => card.issueTypes.map((issueType) => ({
                scope_level: scopeLevel,
                scope_key: scopeKey,
                issue_type: issueType,
                enabled: false
            })));
            const results = yield Promise.allSettled(payloads.map((p) => createCheckConfig(p)));
            const failed = results.filter((r) => r.status === 'rejected').length;
            if (failed > 0) {
                throw new Error(t('check_exclusions.partial_failure', { failed, total: results.length }));
            }
        }),
        onSuccess: () => {
            invalidate();
            setOpenCardKey(null);
            setAddError('');
        },
        onError: (err) => {
            // Some rows may have persisted — refresh so chips reflect reality.
            invalidate();
            setAddError(err.message);
        }
    });
    const deleteMutation = useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ configIds }) {
            const results = yield Promise.allSettled(configIds.map((id) => deleteCheckConfig(id)));
            const failed = results.filter((r) => r.status === 'rejected').length;
            if (failed > 0) {
                throw new Error(t('check_exclusions.delete_partial_failure', { failed, total: results.length }));
            }
        }),
        onSuccess: (_data, { cardKey }) => {
            invalidate();
            setDeleteErrorByCard((prev) => (Object.assign(Object.assign({}, prev), { [cardKey]: '' })));
        },
        onError: (err, { cardKey }) => {
            // Some rows may have been removed — refresh so chips reflect reality.
            invalidate();
            setDeleteErrorByCard((prev) => (Object.assign(Object.assign({}, prev), { [cardKey]: err.message })));
        }
    });
    const handleFlagChange = (card, checked) => {
        updateSettings.mutate({
            settingsUpdate: {
                [card.key]: checked
            }
        });
    };
    const openCard = (_a = CHECK_CARDS.find((c) => c.key === openCardKey)) !== null && _a !== void 0 ? _a : null;
    // Scope keys already excluded for the open card, grouped by axis — passed to
    // the modal so an already-excluded target isn't offered again. A chip only
    // counts as "excluded" once every issue type the card gates has a persisted
    // row; a partial fan-out failure (some issue-type rows missing) keeps the
    // scope key selectable so the user can retry and fill in the gap instead of
    // it silently disappearing from the picker.
    const excludedByLevel = useMemo(() => {
        var _a;
        const map = {};
        if (!openCard)
            return map;
        ((_a = chipsByCard[openCard.key]) !== null && _a !== void 0 ? _a : [])
            .filter((chip) => chip.configIds.length >= openCard.issueTypes.length)
            .forEach((chip) => {
            var _a;
            var _b;
            ((_a = map[_b = chip.scopeLevel]) !== null && _a !== void 0 ? _a : (map[_b] = [])).push(chip.scopeKey);
        });
        return map;
    }, [chipsByCard, openCard]);
    return (React.createElement("div", { className: "aim-issue-config" },
        CHECK_CARDS.map((card) => {
            var _a, _b;
            const value = (_a = settingsData === null || settingsData === void 0 ? void 0 : settingsData[card.key]) !== null && _a !== void 0 ? _a : card.defaultValue;
            const checked = !!value;
            const chips = (_b = chipsByCard[card.key]) !== null && _b !== void 0 ? _b : [];
            const supportsExclusions = card.issueTypes.length > 0;
            const cardTitle = t(card.titleKey);
            return (React.createElement("div", { key: card.key, className: "aim-issue-config__card", "data-screen-label": cardTitle },
                React.createElement("div", { className: "aim-issue-config__card-row" },
                    React.createElement(Checkbox, { id: `ai-inspection-manager-${card.key}`, className: "aim-issue-config__card-checkbox", checked: checked, onChange: (next) => handleFlagChange(card, next), "aria-label": t('check_exclusions.enable_check', { check: cardTitle }) }),
                    React.createElement("div", { className: "aim-issue-config__card-body" },
                        React.createElement("div", { className: "aim-issue-config__card-title" }, cardTitle),
                        React.createElement("p", { className: "aim-issue-config__card-description" }, t(card.descriptionKey)),
                        supportsExclusions && (React.createElement("div", { className: "aim-issue-config__add-exclusion" },
                            React.createElement("button", { type: "button", className: "qmb-control", onClick: () => {
                                    setAddError('');
                                    setOpenCardKey(card.key);
                                } },
                                React.createElement("i", { className: "fa-light fa-circle-plus", "aria-hidden": "true" }),
                                t('check_exclusions.add_exclusion')))),
                        supportsExclusions && deleteErrorByCard[card.key] && (React.createElement(Text, { as: "p", className: "aim-issue-config__delete-error" }, deleteErrorByCard[card.key])),
                        supportsExclusions && chips.length > 0 && (React.createElement("div", { className: "aim-issue-config__exclusions" },
                            React.createElement("span", { className: "aim-issue-config__exclusions-label" }, t('check_exclusions.exclusions_label')),
                            chips.map((chip) => (React.createElement(Tag, { key: `${chip.scopeLevel}:${chip.scopeKey}`, label: chip.label, variant: "gray", onRemove: () => deleteMutation.mutate({ cardKey: card.key, configIds: chip.configIds }) })))))))));
        }),
        openCard && (React.createElement(AddExclusionModal, { isOpen: true, onClose: () => setOpenCardKey(null), checkName: t(openCard.titleKey), accounts: accounts, accountTags: accountTags, buildingTags: buildingTags, excludedByLevel: excludedByLevel, saving: createMutation.isLoading, error: addError, onAdd: (scopeLevel, values) => {
                setAddError('');
                createMutation.mutate({ card: openCard, scopeLevel, values });
            } }))));
}
export default IssuesTypeFlags;
